import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { PinDisplay, PinPad } from '../components/ui/PinPad';
import { UserPair } from '../components/ui/UserChip';
import { Logo } from '../components/Logo';

const PIN_LENGTH = 4;

interface LocationState {
  from?: string;
}

// LoginPin — backup-inloggning med PIN för Henrik och Michelle medan
// magic-link-flödet rullas ut. När SES är konfigurerat på prod kan denna
// rutt tas bort. URL: /login/pin.
export default function LoginPin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading, isAuthenticated, login, loginState } = useAuth();
  const [pin, setPin] = useState('');
  const [state, setState] = useState<'idle' | 'error' | 'success'>('idle');
  const [statusText, setStatusText] = useState('Enter your PIN');

  const target = (location.state as LocationState | null)?.from || '/';

  useEffect(() => {
    if (loginState.error) {
      const err = loginState.error;
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === 'INVALID_PIN' || (err instanceof ApiError && err.status === 401)) {
        setStatusText('Incorrect PIN');
      } else if (err instanceof ApiError && err.status === 429) {
        setStatusText('Too many attempts');
      } else {
        setStatusText('Could not log in');
      }
      setState('error');
      const t = setTimeout(() => {
        setPin('');
        setState('idle');
        setStatusText('Try again');
        loginState.reset();
      }, 700);
      return () => clearTimeout(t);
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState.error]);

  if (!isLoading && isAuthenticated && user) {
    return <Navigate to={target} replace />;
  }

  async function handleSubmit(submittedPin: string) {
    if (loginState.isPending) return;
    setStatusText('Checking…');
    setState('idle');
    try {
      await login(submittedPin);
      setStatusText('Welcome back');
      setState('success');
      setTimeout(() => navigate(target, { replace: true }), 350);
    } catch {
      // hanteras via loginState.error
    }
  }

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center relative overflow-hidden">
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: [
            'radial-gradient(ellipse at 30% 20%, rgba(95,168,211,0.12), transparent 50%)',
            'radial-gradient(ellipse at 70% 80%, rgba(230,57,70,0.12), transparent 50%)',
            'radial-gradient(ellipse at center, rgba(0,0,0,0.6), rgba(0,0,0,0.95))',
          ].join(','),
        }}
      />

      <div
        className={[
          'relative z-10 w-[420px] max-w-[calc(100vw-32px)]',
          'rounded-[28px] px-8 sm:px-11 pt-12 pb-10',
          'bg-[rgba(15,15,15,0.7)] backdrop-blur-[40px]',
          'border border-white/[0.08]',
          'shadow-[0_30px_80px_-10px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.08)]',
        ].join(' ')}
      >
        <div className="flex justify-center mb-1.5">
          <Logo size="lg" color="#ededed" />
        </div>
        <div className="text-center text-[11px] text-loupe-text-3 tracking-[0.3em] uppercase mb-9">
          PIN sign-in · backup
        </div>

        <PinDisplay filled={pin.length} total={PIN_LENGTH} state={state} />
        <div
          className={[
            'text-center text-xs uppercase tracking-[0.15em] mt-2 mb-7 h-4 transition-colors',
            state === 'error'
              ? 'text-michelle'
              : state === 'success'
                ? 'text-emerald-400'
                : 'text-loupe-text-3',
          ].join(' ')}
        >
          {statusText}
        </div>

        <PinPad
          pin={pin}
          onChange={setPin}
          onSubmit={handleSubmit}
          length={PIN_LENGTH}
          disabled={loginState.isPending || state === 'success'}
        />

        <div className="mt-8 pt-6 border-t border-white/[0.06] flex justify-between items-center text-[11px] text-loupe-text-3 tracking-[0.05em]">
          <UserPair size="md" />
          <Link
            to="/login"
            className="text-loupe-text-2 hover:text-white transition-colors py-2 px-1 min-h-[36px] tracking-[0.15em] uppercase"
          >
            ← Email sign-in
          </Link>
        </div>
      </div>
    </div>
  );
}
